'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import {
  IntegrationUnavailableDialog,
  type IntegrationUnavailableCopy,
} from '@/components/integrations/shared/integration-unavailable-dialog'
import { loadExternalScript } from '@/components/integrations/shared/load-script'
import { intercomAppId } from '@/config/env.public'
import type { Locale } from '@/i18n/routing'

type IntercomContextValue = {
  open: () => void
  /** Closes the Messenger panel, if it is open. The launcher stays. */
  hide: () => void
  /**
   * Hides or restores the launcher. Intercom draws it at a z-index nothing else can reach, so on a
   * phone it lands on top of the demo planner — a chat bubble over the calendar someone opened to
   * pick a date.
   */
  setLauncherVisible: (visible: boolean) => void
}

const IntercomContext = createContext<IntercomContextValue | null>(null)
const intercomScriptId = 'intercom-widget-script'
/**
 * Bounds a load that never settles, so a later chat click downloads afresh instead of waiting on a
 * dead request. Generous: the widget is ~300 KB and usually loads in the background, unwatched.
 */
const intercomLoadTimeoutMs = 15_000

/**
 * Everything a logged-out visitor's Messenger is told, and nothing more — the workspace contract
 * (`docs/contracts/intercom/identity.md`): no user id, no JWT, no attributes.
 */
const messengerSettings = (hideLauncher: boolean, locale: Locale) => ({
  app_id: intercomAppId,
  hide_default_launcher: hideLauncher,
  language_override: locale,
})

export function IntercomProvider({
  locale,
  unavailableCopy,
  children,
}: {
  locale: Locale
  unavailableCopy: IntegrationUnavailableCopy
  children: ReactNode
}) {
  const bootedRef = useRef(false)
  /**
   * The page's language, handed to the Messenger as `language_override`. A logged-out visitor has
   * no Intercom language of their own, so without it the Messenger follows the workspace default,
   * Dutch, and greeted visitors on English pages in Dutch.
   */
  const localeRef = useRef(locale)
  /**
   * The launcher visibility last asked for through `setLauncherVisible`. Every write of
   * `intercomSettings` — the boot included — carries it, because the planner can ask before the
   * widget is there: a campaign link opens it on load, seconds before the deferred boot. A boot
   * that reset the launcher to visible put the chat bubble back on top of the calendar.
   */
  const launcherVisibleRef = useRef(true)
  const [loadFailed, setLoadFailed] = useState(false)

  const ensureIntercomLoaded = useCallback(() => {
    if (typeof window === 'undefined') {
      return Promise.resolve()
    }

    window.intercomSettings = messengerSettings(!launcherVisibleRef.current, localeRef.current)

    if (window.Intercom && document.getElementById(intercomScriptId)) {
      return Promise.resolve()
    }

    return loadExternalScript({
      id: intercomScriptId,
      src: `https://widget.intercom.io/widget/${intercomAppId}`,
      timeoutMs: intercomLoadTimeoutMs,
    })
  }, [])

  // A locale switch keeps this provider mounted, so a booted Messenger is told the new language.
  useEffect(() => {
    localeRef.current = locale
    if (!bootedRef.current || !window.Intercom) {
      return
    }
    window.intercomSettings = messengerSettings(!launcherVisibleRef.current, locale)
    window.Intercom('update', window.intercomSettings)
  }, [locale])

  // The widget is ~300 KB of third-party JavaScript and the only external script on the critical
  // path. Nobody opens a chat in the first second of a page they have not read yet, so it waits
  // for a sign of life — a scroll, a pointer, a key — and for the browser to be idle. Anyone who
  // clicks a chat link before that gets it loaded on demand by `open()` below.
  useEffect(() => {
    let cancelled = false

    const boot = () => {
      if (cancelled) {
        return
      }
      void ensureIntercomLoaded()
        .then(() => {
          if (cancelled || !window.Intercom) {
            return
          }

          if (bootedRef.current) {
            window.Intercom('update', window.intercomSettings)
            return
          }

          window.Intercom('boot', window.intercomSettings)
          bootedRef.current = true
        })
        .catch(() => undefined)
    }

    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const
    // A visitor who reads without touching anything still gets the launcher, just not at load.
    const fallback = window.setTimeout(() => start(), 8000)
    const start = () => {
      // Once is enough: whichever comes first — a gesture or the fallback — starts the boot, and the
      // other must not run it a second time.
      window.clearTimeout(fallback)
      events.forEach((event) => window.removeEventListener(event, start))
      const idle = window.requestIdleCallback
      if (typeof idle === 'function') {
        idle(boot, { timeout: 2000 })
      } else {
        window.setTimeout(boot, 500)
      }
    }

    events.forEach((event) => window.addEventListener(event, start, { once: true, passive: true }))

    return () => {
      cancelled = true
      window.clearTimeout(fallback)
      events.forEach((event) => window.removeEventListener(event, start))
    }
  }, [ensureIntercomLoaded])

  const value = useMemo(
    () => ({
      setLauncherVisible: (visible: boolean) => {
        // Remembered even when the widget is not there yet, so the boot honours it.
        launcherVisibleRef.current = visible
        if (!window.Intercom) {
          return
        }
        window.intercomSettings = messengerSettings(!visible, localeRef.current)
        window.Intercom('update', window.intercomSettings)
      },
      open: () => {
        void ensureIntercomLoaded()
          .then(() => {
            if (!bootedRef.current && window.Intercom) {
              window.Intercom('boot', window.intercomSettings)
              bootedRef.current = true
            }
            window.Intercom?.('show')
          })
          // The visitor asked for the chat, so they hear that it did not come — often an ad blocker.
          // The background boot above stays silent: nobody asked for anything yet.
          .catch(() => setLoadFailed(true))
      },
      hide: () => {
        window.Intercom?.('hide')
      },
    }),
    [ensureIntercomLoaded],
  )

  return (
    <IntercomContext.Provider value={value} data-sentry-element="IntercomContext.Provider" data-sentry-component="IntercomProvider" data-sentry-source-file="intercom-provider.tsx">
      {children}
      <IntegrationUnavailableDialog open={loadFailed} onClose={() => setLoadFailed(false)} copy={unavailableCopy} data-sentry-element="IntegrationUnavailableDialog" data-sentry-source-file="intercom-provider.tsx" />
    </IntercomContext.Provider>
  )
}

export function useIntercom() {
  const context = useContext(IntercomContext)
  if (!context) {
    throw new Error('useIntercom must be used within an IntercomProvider')
  }
  return context
}
